import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, User, ArrowLeft, Check, X, AlertCircle, Calendar, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { ThemeToggle } from '../components/ThemeToggle';
import { Logo } from '../components/Logo';
import { toast } from 'sonner';

// Apple icon component
const AppleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

// Google icon component
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Email validation regex - defined outside component to prevent recreation
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({ firstName: false, lastName: false, email: false, password: false, dob: false, legal: false });

  // Validation states
  const [validation, setValidation] = useState({
    firstName: { valid: false, message: '' },
    lastName: { valid: false, message: '' },
    email: { valid: false, message: '' },
    password: { valid: false, message: '' },
    dob: { valid: true, message: '' },
    legal: { valid: false, message: '' }
  });

  // Calculate age from DOB
  const calculateAge = (dob) => {
    if (!dob) return null;
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const age = calculateAge(dateOfBirth);

  // Validate fields
  useEffect(() => {
    setValidation({
      firstName: {
        valid: firstName.trim().length >= 1,
        message: firstName.trim().length === 0 ? 'First name is required' : ''
      },
      lastName: {
        valid: lastName.trim().length >= 1,
        message: lastName.trim().length === 0 ? 'Last name is required' : ''
      },
      email: {
        valid: EMAIL_REGEX.test(email),
        message: email.length === 0 ? 'Email is required' : !EMAIL_REGEX.test(email) ? 'Please enter a valid email' : ''
      },
      password: {
        valid: password.length >= 6,
        message: password.length === 0 ? 'Password is required' : password.length < 6 ? 'Password must be at least 6 characters' : ''
      },
      dob: {
        valid: !dateOfBirth || (age !== null && age >= 13),
        message: age !== null && age < 13 ? 'You must be at least 13 years old' : ''
      },
      legal: {
        valid: legalAccepted,
        message: !legalAccepted ? 'You must accept the Terms and Privacy Policy' : ''
      }
    });
  }, [firstName, lastName, email, password, dateOfBirth, legalAccepted, age]);

  const allValid = validation.firstName.valid && validation.lastName.valid && 
                   validation.email.valid && validation.password.valid && 
                   validation.dob.valid && validation.legal.valid;

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ firstName: true, lastName: true, email: true, password: true, dob: true, legal: true });
    
    if (!allValid) {
      if (!legalAccepted) {
        toast.error('Please accept the Terms and Privacy Policy');
      } else {
        toast.error('Please fix the errors before continuing');
      }
      return;
    }

    setLoading(true);
    try {
      await register(email, password, firstName, lastName, dateOfBirth, legalAccepted);
      toast.success('Account created! Let\'s set up your profile.');
      navigate('/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    toast.info(`${provider} login coming soon!`);
    // In production, integrate with actual OAuth providers
  };

  // Validation indicator component
  const renderValidationIndicator = (field) => {
    if (!touched[field]) return null;
    return validation[field].valid ? (
      <Check size={16} className="text-[#8BB89F]" />
    ) : (
      <X size={16} className="text-[#D54B48]" />
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="flex items-center justify-between p-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/')}
          className="rounded-full"
        >
          <ArrowLeft size={20} />
        </Button>
        <ThemeToggle />
      </header>

      {/* Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          {/* Logo */}
          <div className="text-center mb-6">
            <Logo className="h-12 mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-bold">Create Account</h1>
            <p className="text-muted-foreground text-sm">Start your 7-day free trial</p>
          </div>

          {/* Social Login */}
          <div className="space-y-3 mb-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleSocialLogin('Apple')}
              className="w-full rounded-xl h-12 font-medium"
              data-testid="apple-login-btn"
            >
              <AppleIcon />
              <span className="ml-2">Continue with Apple</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleSocialLogin('Google')}
              className="w-full rounded-xl h-12 font-medium"
              data-testid="google-login-btn"
            >
              <GoogleIcon />
              <span className="ml-2">Continue with Google</span>
            </Button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Form */}
          <Card className="glass-card p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* First & Last Name Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <div className="relative">
                    <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="firstName"
                      type="text"
                      placeholder="John"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      onBlur={() => handleBlur('firstName')}
                      className={`pl-10 pr-8 rounded-xl h-12 ${
                        touched.firstName && !validation.firstName.valid ? 'border-[#D54B48] focus-visible:ring-[#D54B48]' : ''
                      }`}
                      data-testid="first-name-input"
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      {renderValidationIndicator('firstName')}
                    </div>
                  </div>
                  {touched.firstName && validation.firstName.message && (
                    <motion.p 
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-xs text-[#D54B48] flex items-center gap-1"
                    >
                      <AlertCircle size={12} />
                      {validation.firstName.message}
                    </motion.p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <div className="relative">
                    <Input
                      id="lastName"
                      type="text"
                      placeholder="Doe"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      onBlur={() => handleBlur('lastName')}
                      className={`pr-8 rounded-xl h-12 ${
                        touched.lastName && !validation.lastName.valid ? 'border-[#D54B48] focus-visible:ring-[#D54B48]' : ''
                      }`}
                      data-testid="last-name-input"
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      {renderValidationIndicator('lastName')}
                    </div>
                  </div>
                  {touched.lastName && validation.lastName.message && (
                    <motion.p 
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-xs text-[#D54B48] flex items-center gap-1"
                    >
                      <AlertCircle size={12} />
                      {validation.lastName.message}
                    </motion.p>
                  )}
                </div>
              </div>

              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => handleBlur('email')}
                    className={`pl-10 pr-10 rounded-xl h-12 ${
                      touched.email && !validation.email.valid ? 'border-[#D54B48] focus-visible:ring-[#D54B48]' : ''
                    }`}
                    data-testid="email-input"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {renderValidationIndicator('email')}
                  </div>
                </div>
                {touched.email && validation.email.message && (
                  <motion.p 
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-xs text-[#D54B48] flex items-center gap-1"
                  >
                    <AlertCircle size={12} />
                    {validation.email.message}
                  </motion.p>
                )}
              </div>

              {/* Date of Birth Field */}
              <div className="space-y-2">
                <Label htmlFor="dob">Date of Birth</Label>
                <div className="relative">
                  <Calendar size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="dob"
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    onBlur={() => handleBlur('dob')}
                    max={new Date().toISOString().split('T')[0]}
                    className={`pl-10 pr-10 rounded-xl h-12 ${
                      touched.dob && !validation.dob.valid ? 'border-[#D54B48] focus-visible:ring-[#D54B48]' : ''
                    }`}
                    data-testid="dob-input"
                  />
                  {age !== null && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                        {age} yrs
                      </span>
                    </div>
                  )}
                </div>
                {touched.dob && validation.dob.message && (
                  <motion.p 
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-xs text-[#D54B48] flex items-center gap-1"
                  >
                    <AlertCircle size={12} />
                    {validation.dob.message}
                  </motion.p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={() => handleBlur('password')}
                    className={`pl-10 pr-20 rounded-xl h-12 ${
                      touched.password && !validation.password.valid ? 'border-[#D54B48] focus-visible:ring-[#D54B48]' : ''
                    }`}
                    data-testid="password-input"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                    {renderValidationIndicator('password')}
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                {touched.password && validation.password.message && (
                  <motion.p 
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-xs text-[#D54B48] flex items-center gap-1"
                  >
                    <AlertCircle size={12} />
                    {validation.password.message}
                  </motion.p>
                )}
                {/* Password strength indicator */}
                {password.length > 0 && (
                  <div className="flex gap-1 mt-1">
                    <div className={`h-1 flex-1 rounded-full ${password.length >= 2 ? 'bg-[#D54B48]' : 'bg-muted'}`} />
                    <div className={`h-1 flex-1 rounded-full ${password.length >= 4 ? 'bg-[#F8B64C]' : 'bg-muted'}`} />
                    <div className={`h-1 flex-1 rounded-full ${password.length >= 6 ? 'bg-[#8BB89F]' : 'bg-muted'}`} />
                    <div className={`h-1 flex-1 rounded-full ${password.length >= 8 ? 'bg-[#8BB89F]' : 'bg-muted'}`} />
                  </div>
                )}
              </div>

              {/* Legal Acceptance */}
              <div className={`p-3 rounded-xl border ${touched.legal && !legalAccepted ? 'border-[#D54B48] bg-[#D54B48]/5' : 'border-border bg-muted/30'}`}>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="legal"
                    checked={legalAccepted}
                    onCheckedChange={(checked) => {
                      setLegalAccepted(checked);
                      setTouched(prev => ({ ...prev, legal: true }));
                    }}
                    data-testid="legal-checkbox"
                  />
                  <Label htmlFor="legal" className="text-sm cursor-pointer whitespace-nowrap">
                    I agree to the <Link to="/terms" className="text-primary hover:underline">Terms & Conditions</Link> and <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
                  </Label>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90 text-white rounded-full py-6"
                data-testid="register-btn"
              >
                {loading ? 'Creating account...' : 'Start Wellness Journey'}
              </Button>
            </form>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </motion.div>
      </main>
    </div>
  );
}
